import {useEffect, useState} from 'react'
import {ToastContainer} from 'react-toastify'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import clsx from 'clsx'
import 'react-toastify/dist/ReactToastify.css'
import {KTIcon} from '../../../_websoft/helpers'
import notifyToast from '../../components/Toast/Toast'
import TitleComponent from '../../components/TitleComponent/TitleComponent'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import {
    fetchThermoBoardFmkNalog,
    saveThermoBoardFmkNalog,
    updateThermoBoardFmkNalog,
} from '../../api/thermoboardNalozi'
import {getFmkNalogaStavkaData} from '../../api/fmkNalozi'
import KomercijalistePanelaDropdown
    from '../paneli/components/KomercijalistePanelaDropdown/KomercijalistePanelaDropdown'
import PanelTipDropdown from '../paneli/components/PanelTipDropdown/PanelTipDropdown'
import {useParams} from 'react-router-dom'
import {useNavigate} from 'react-router-dom'

type ThermoBoardFmkFormValues = {
    fmk_nalog: string
    stavka: string
    fmk_partner: string
    nazivKupca: string
    tip_panela: string
    komercijalista: string
    rok_isporuke: string
    kolicina: string
    isporucena_kolicina: string
    planirana_proizvodnja: string
    planirana_kolicina_za_nalog: string
    status: string
}

const initialValues: ThermoBoardFmkFormValues = {
    fmk_nalog: '',
    stavka: '',
    fmk_partner: '',
    nazivKupca: '',
    tip_panela: '',
    komercijalista: '',
    rok_isporuke: '',
    kolicina: '',
    isporucena_kolicina: '',
    planirana_proizvodnja: '',
    planirana_kolicina_za_nalog: '',
    status: '1',
}

const getTipPanelaValue = (data: any) =>
  data?.tip_panela_id?.toString() ??
  data?.TipPanela?.toString() ??
  data?.TipId?.toString() ??
  data?.tip_panela?.Id?.toString() ??
  data?.tip_panela?.id?.toString() ??
  (typeof data?.tip_panela === 'number' || typeof data?.tip_panela === 'string'
    ? data.tip_panela.toString()
    : '')

const getDateValue = (value: string | null | undefined) => {
    if (!value) {
        return ''
    }

    return value.slice(0, 10)
}

const validationSchema = Yup.object({
    fmk_nalog: Yup.string().max(255, 'Maksimalno 255 karaktera').required('Obavezno polje'),
    stavka: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Stavka mora biti cijeli broj')
        .integer('Stavka mora biti cijeli broj')
        .nullable()
        .notRequired(),
    fmk_partner: Yup.number().typeError('ID kupca FMK mora biti broj').required('Obavezno polje'),
    nazivKupca: Yup.string().max(255, 'Maksimalno 255 karaktera').required('Obavezno polje'),
    tip_panela: Yup.number().typeError('Tip panela mora biti broj').required('Obavezno polje'),
    komercijalista: Yup.string().max(255, 'Maksimalno 255 karaktera').required('Obavezno polje'),
    rok_isporuke: Yup.date().typeError('Rok isporuke nije ispravan').required('Obavezno polje'),
    kolicina: Yup.number()
        .typeError('Kolicina mora biti broj')
        .positive('Kolicina mora biti veca od 0')
        .required('Obavezno polje'),
    isporucena_kolicina: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Isporucena kolicina mora biti broj')
        .nullable()
        .notRequired(),
    planirana_proizvodnja: Yup.date()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Planirana proizvodnja nije ispravna')
        .nullable()
        .notRequired(),
    planirana_kolicina_za_nalog: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Planirana kolicina za nalog mora biti broj')
        .min(0, 'Planirana kolicina za nalog ne moze biti negativna')
        .nullable()
        .notRequired(),
    status: Yup.string().oneOf(['0', '1'], 'Status nije ispravan').required('Obavezno polje'),
})

const ThermoBoardFmkDetails: React.FC = () => {
    const {id} = useParams()
    const idFromUrl = Number(id)
    const isEditMode = Boolean(idFromUrl)
    const [loading, setLoading] = useState(isEditMode)
    const [nalogLookupLoading, setNalogLookupLoading] = useState(false)
    const [formValues, setFormValues] = useState<ThermoBoardFmkFormValues>(initialValues)
    const navigate = useNavigate()

    useEffect(() => {
        let mounted = true

        const loadFmkNalog = async () => {
            if (!isEditMode) {
                setLoading(false)
                return
            }

            try {
                const data = await fetchThermoBoardFmkNalog(idFromUrl)

                if (mounted) {
                    setFormValues({
                        fmk_nalog: data?.fmk_nalog ?? '',
                        stavka: data?.stavka?.toString() ?? '',
                        fmk_partner:
                            data?.idKupacFMK?.toString() ??
                            data?.IdKupacFMK?.toString() ??
                            data?.fmk_partner?.toString() ??
                            data?.fmk_partner_id?.toString() ??
                            data?.partner?.Id?.toString() ??
                            data?.partner?.id?.toString() ??
                            '',
                        nazivKupca:
                            data?.nazivKupca ??
                            data?.KupacNaziv ??
                            data?.kupac?.Naziv ??
                            data?.kupac?.naziv ??
                            data?.partner?.Naziv ??
                            data?.partner?.naziv ??
                            '',
            tip_panela: getTipPanelaValue(data),
                        komercijalista: data?.komercijalista?.toString() ?? '',
                        rok_isporuke: data?.rok_isporuke ? data.rok_isporuke.slice(0, 10) : '',
                        kolicina: data?.kolicina?.toString() ?? '',
                        isporucena_kolicina: data?.isporucena_kolicina?.toString() ?? '',
                        planirana_proizvodnja: getDateValue(data?.planirana_proizvodnja),
                        planirana_kolicina_za_nalog: data?.planirana_kolicina_za_nalog?.toString() ?? '',
                        status: data?.status?.toString() ?? '1',
                    })
                }
            } catch (error: any) {
                notifyToast(false, error?.response?.data?.message || 'FMK nalog nije pronadjen.')
            } finally {
                if (mounted) {
                    setLoading(false)
                }
            }
        }

        loadFmkNalog()

        return () => {
            mounted = false
        }
    }, [idFromUrl, isEditMode])

    const formik = useFormik<ThermoBoardFmkFormValues>({
        initialValues: formValues,
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values, {resetForm, setSubmitting}) => {
            try {
                const payload = {
                    fmk_nalog: values.fmk_nalog,
                    stavka: values.stavka ? Number(values.stavka) : null,
                    idKupacFMK: values.fmk_partner,
                    nazivKupca: values.nazivKupca,
                    tip_panela: values.tip_panela,
                    komercijalista: values.komercijalista,
                    rok_isporuke: values.rok_isporuke,
                    kolicina: Number(values.kolicina),
                    isporucena_kolicina: values.isporucena_kolicina
                        ? Number(values.isporucena_kolicina)
                        : null,
                    planirana_proizvodnja: values.planirana_proizvodnja || null,
                    planirana_kolicina_za_nalog: values.planirana_kolicina_za_nalog
                        ? Number(values.planirana_kolicina_za_nalog)
                        : null,
                    status: Number(values.status),
                }

                const response = isEditMode
                    ? await updateThermoBoardFmkNalog(payload, idFromUrl)
                    : await saveThermoBoardFmkNalog(payload)

                if (response?.success || response?.error === 0) {
                    notifyToast(true, response?.message || 'FMK nalog je uspjesno snimljen.')
                    if (!isEditMode) {
                        resetForm()
                    }
                    setTimeout(() => {
                        navigate(-1)
                    }, 1500) // small delay so user sees toast
                    return
                }

                notifyToast(false, response?.message || 'Snimanje FMK naloga nije uspjelo.')
            } catch (error: any) {
                notifyToast(
                    false,
                    error?.response?.data?.message || error?.message || 'Greska pri snimanju FMK naloga.'
                )
            } finally {
                setSubmitting(false)
            }
        },
    })

    const hasError = (field: keyof ThermoBoardFmkFormValues) =>
        Boolean(formik.touched[field] && formik.errors[field])

    const fieldClass = (field: keyof ThermoBoardFmkFormValues) =>
        clsx('form-control form-control-lg form-control-solid', {
            'is-invalid': hasError(field),
            'is-valid': formik.touched[field] && !formik.errors[field],
        })

    const renderError = (field: keyof ThermoBoardFmkFormValues) => {
        if (!hasError(field)) {
            return null
        }

        return (
            <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>{formik.errors[field]}</div>
            </div>
        )
    }

    const getKupacFromNalogResponse = (response: any) => {
        const responseData = response?.data ?? response
        const nalog = responseData?.nalog ?? responseData?.data?.nalog ?? responseData
        const idKupacFMK =
            nalog?.idKupacFMK ??
            nalog?.IdKupacFMK ??
            nalog?.KupacIdFMK ??
            nalog?.kupacIdFMK ??
            nalog?.IdKupca ??
            nalog?.idKupca ??
            nalog?.KupacId ??
            nalog?.kupacId ??
            nalog?.IdPartnera ??
            nalog?.idPartnera ??
            ''
        const nazivKupca =
            nalog?.nazivKupca ??
            nalog?.NazivKupca ??
            nalog?.KupacNaziv ??
            nalog?.kupacNaziv ??
            nalog?.Kupac ??
            nalog?.kupac ??
            nalog?.partner?.Naziv ??
            nalog?.partner?.naziv ??
            ''

        return {
            idKupacFMK: idKupacFMK?.toString() ?? '',
            nazivKupca: nazivKupca?.toString() ?? '',
        }
    }

    const loadKupacForNalog = async () => {
        const nalog = formik.values.fmk_nalog?.trim()

        if (!nalog) {
            formik.setFieldValue('fmk_partner', '')
            formik.setFieldValue('nazivKupca', '')
            return
        }

        setNalogLookupLoading(true)

        try {
            const response = await getFmkNalogaStavkaData({nalog})
            const kupac = getKupacFromNalogResponse(response)

            if (!kupac.idKupacFMK || !kupac.nazivKupca) {
                formik.setFieldValue('fmk_partner', '')
                formik.setFieldValue('nazivKupca', kupac.nazivKupca)
                notifyToast(false, 'FMK nalog je pronadjen, ali API nije vratio ID kupca i naziv kupca.')
                return
            }

            formik.setFieldValue('fmk_partner', kupac.idKupacFMK)
            formik.setFieldValue('nazivKupca', kupac.nazivKupca)
            formik.setFieldTouched('fmk_partner', true, false)
            formik.setFieldTouched('nazivKupca', true, false)
        } catch (error: any) {
            formik.setFieldValue('fmk_partner', '')
            formik.setFieldValue('nazivKupca', '')
            notifyToast(false, error?.response?.data?.message || 'FMK nalog nije pronadjen.')
        } finally {
            setNalogLookupLoading(false)
        }
    }

    return (
        <div className='card shadow-sm mb-5 mb-xl-10'>
            <ToastContainer/>

            <div className='card-header border-0 pt-6'>
                <div className='card-title flex-column align-items-start m-0'>
                    <TitleComponent
                        name={isEditMode ? 'Izmjena Thermo Board FMK naloga' : 'novi Thermo Board FMK nalog'}
                    />
                    <span className='text-muted fw-semibold fs-7 mt-1'>
            Unos osnovnih podataka za pripremu ThermoBoard FMK naloga.
          </span>
                </div>
            </div>

            {loading ? (
                <LoadingComponent/>
            ) : (
                <form onSubmit={formik.handleSubmit} noValidate className='form'>
                    <div className='card-body border-top p-9'>
                        <div className='rounded bg-light p-6 mb-8'>
                            <div className='d-flex align-items-start gap-4'>
                                <div className='symbol symbol-45px'>
                                    <div className='symbol-label bg-light-primary'>
                                        <KTIcon iconName='document' className='fs-2 text-primary'/>
                                    </div>
                                </div>

                                <div>
                                    <div className='fs-5 fw-bold text-gray-900 mb-1'>Podaci o FMK nalogu</div>
                                    <div className='text-muted fw-semibold fs-7'>
                                        Popunite komercijalne podatke i rok isporuke. Da bi se
                                        kreirao Thermo Board nalog, potrebno je otvoriti nalog u FMK, zatim povuci nalog
                                        u Websoft.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='mb-10'>
                            <div className='row g-6'>
                                <div className='col-xl-4 col-lg-6'>
                                    <label className='form-label required fw-bold fs-6'>Broj FMK naloga</label>
                                    <input
                                        type='text'
                                        placeholder='Broj FMK naloga'
                                        name='fmk_nalog'
                                        value={formik.values.fmk_nalog}
                                        onChange={(event) => {
                                            formik.handleChange(event)
                                            formik.setFieldValue('fmk_partner', '')
                                            formik.setFieldValue('nazivKupca', '')
                                        }}
                                        onBlur={(event) => {
                                            formik.handleBlur(event)
                                            loadKupacForNalog()
                                        }}
                                        className={fieldClass('fmk_nalog')}
                                    />
                                    {renderError('fmk_nalog')}
                                </div>

                                <div className='col-xl-4 col-lg-6'>
                                    <label className='form-label fw-bold fs-6'>Stavka</label>
                                    <input
                                        type='number'
                                        step='1'
                                        placeholder='Stavka'
                                        {...formik.getFieldProps('stavka')}
                                        className={fieldClass('stavka')}
                                    />
                                    {renderError('stavka')}
                                </div>

                                <div className='col-xl-4 col-lg-6'>
                                    <label className='form-label required fw-bold fs-6'>ID kupca FMK</label>
                                    <input
                                        type='text'
                                        value={formik.values.fmk_partner}
                                        className={fieldClass('fmk_partner')}
                                        placeholder={nalogLookupLoading ? 'Ucitavanje...' : 'Automatski iz FMK naloga'}
                                        disabled
                                    />
                                    {renderError('fmk_partner')}
                                </div>

                                <div className='col-xl-4 col-lg-6'>
                                    <label className='form-label required fw-bold fs-6'>Naziv kupca</label>
                                    <input
                                        type='text'
                                        value={formik.values.nazivKupca}
                                        className={fieldClass('nazivKupca')}
                                        placeholder={nalogLookupLoading ? 'Ucitavanje...' : 'Automatski iz FMK naloga'}
                                        disabled
                                    />
                                    {renderError('nazivKupca')}
                                </div>

                                <div className='col-xl-4 col-lg-6'>
                                    <label className='form-label required fw-bold fs-6'>Komercijalista</label>
                                    <KomercijalistePanelaDropdown
                                        value={formik.values.komercijalista}
                                        onChange={(_, value) => formik.setFieldValue('komercijalista', value)}
                                        errors={formik.errors.komercijalista}
                                        touched={formik.touched.komercijalista}
                                        selectName='komercijalista'
                                    />
                                </div>

                                <div className='col-xl-4 col-lg-6'>
                                    <label className='form-label required fw-bold fs-6'>Tip panela</label>
                                    <PanelTipDropdown
                                        value={formik.values.tip_panela}
                                        onChange={(_, value) => formik.setFieldValue('tip_panela', value)}
                                        errors={formik.errors.tip_panela}
                                        touched={formik.touched.tip_panela}
                                        selectName='tip_panela'
                                    />
                                </div>

                                <div className='col-xl-4 col-lg-6'>
                                    <label className='form-label required fw-bold fs-6'>Rok isporuke</label>
                                    <input
                                        type='date'
                                        {...formik.getFieldProps('rok_isporuke')}
                                        className={fieldClass('rok_isporuke')}
                                    />
                                    {renderError('rok_isporuke')}
                                </div>

                                <div className='col-xl-4 col-lg-6'>
                                    <label className='form-label required fw-bold fs-6'>Kolicina</label>
                                    <input
                                        type='number'
                                        min='0'
                                        step='0.01'
                                        placeholder='Kolicina'
                                        {...formik.getFieldProps('kolicina')}
                                        className={fieldClass('kolicina')}
                                    />
                                    {renderError('kolicina')}
                                </div>

                                <div className='col-xl-4 col-lg-6'>
                                    <label className='form-label fw-bold fs-6'>Planirana proizvodnja</label>
                                    <input
                                        type='date'
                                        {...formik.getFieldProps('planirana_proizvodnja')}
                                        className={fieldClass('planirana_proizvodnja')}
                                    />
                                    {renderError('planirana_proizvodnja')}
                                </div>

                                <div className='col-xl-4 col-lg-6'>
                                    <label className='form-label fw-bold fs-6'>Planirana kolicina za nalog</label>
                                    <input
                                        type='number'
                                        min='0'
                                        step='0.01'
                                        placeholder='Planirana kolicina za nalog'
                                        {...formik.getFieldProps('planirana_kolicina_za_nalog')}
                                        className={fieldClass('planirana_kolicina_za_nalog')}
                                    />
                                    {renderError('planirana_kolicina_za_nalog')}
                                </div>

                                <div className='col-xl-4 col-lg-6'>
                                    <label className='form-label required fw-bold fs-6'>Status</label>
                                    <select
                                        {...formik.getFieldProps('status')}
                                        className={clsx('form-select form-select-lg form-select-solid', {
                                            'is-invalid': hasError('status'),
                                            'is-valid': formik.touched.status && !formik.errors.status,
                                        })}
                                    >
                                        <option value='1'>Aktivan nalog</option>
                                        <option value='0'>Arhiviran nalog</option>
                                    </select>
                                    {renderError('status')}
                                </div>

                            </div>
                        </div>
                    </div>

                    <div
                        className='card-footer d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-4 py-6 px-9'>
                        <div className='text-muted fw-semibold fs-7'>
                            Sva oznacena polja su obavezna za unos FMK naloga.
                        </div>

                        <button
                            type='submit'
                            className='btn btn-primary fw-bold px-8'
                            disabled={formik.isSubmitting}
                        >
                            {!formik.isSubmitting && 'Potvrdi'}
                            {formik.isSubmitting && (
                                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
                            )}
                        </button>
                    </div>
                </form>
            )}
        </div>
    )
}

export default ThermoBoardFmkDetails
